import { Controller } from "@hotwired/stimulus"

// A full-screen viewer for a post's images (reflections/_media_grid). Click a
// tile to open it at that image; arrow keys move between the images of that
// one post; Escape, the close button, or a click on the backdrop closes it.
//
// Built into <body> rather than inline, for the same reason
// persona_media_controller does it: the grid lives inside a scrolling feed
// column, and any ancestor with overflow or a transform clips a position:
// fixed child. It is deliberately far smaller than that controller — no
// segmented panels, no overflow menu, no per-item actions — so the two stay
// separate rather than one growing a mode for the other.
//
// One overlay exists at a time across the whole page, even though this
// controller mounts once per post: opening a second closes the first.
export default class extends Controller {
    static targets = ["tile"]

    disconnect() {
        this.close()
    }

    open(event) {
        event.preventDefault()
        // The grid sits inside .reflection-card-main, which app-card-link
        // turns into "click anywhere to open the thread". Without this the
        // same click both opens the lightbox and navigates away underneath it.
        event.stopPropagation()
        this._items = this.tileTargets.map((tile) => ({
            url: tile.dataset.fullUrl,
            caption: tile.dataset.caption || ""
        }))
        this._index = Number(event.currentTarget.dataset.index || 0)
        this._opener = event.currentTarget
        this._build()
        this._render()
    }

    close() {
        if (!this._overlay) return
        this._overlay.remove()
        this._overlay = null
        document.removeEventListener("keydown", this._onKeydown)
        document.body.classList.remove("image-lightbox-open")
        // Return focus to the tile that opened it — without this, a keyboard
        // user lands back at the top of the document on every close.
        if (this._opener && document.contains(this._opener)) this._opener.focus()
        this._opener = null
    }

    _step(delta) {
        if (!this._items || this._items.length < 2) return
        this._index = (this._index + delta + this._items.length) % this._items.length
        this._render()
    }

    _build() {
        document.querySelector(".image-lightbox")?.remove()

        const overlay = document.createElement("div")
        overlay.className = "image-lightbox"
        overlay.setAttribute("role", "dialog")
        overlay.setAttribute("aria-modal", "true")
        overlay.setAttribute("aria-label", "Image viewer")
        // Anything that isn't the image itself or a control closes — the
        // backdrop is most of the overlay's area, so this is the primary
        // dismiss gesture on a pointer device.
        overlay.addEventListener("click", (e) => {
            if (e.target === overlay || e.target.classList.contains("image-lightbox-stage")) this.close()
        })

        overlay.innerHTML = `
            <button type="button" class="image-lightbox-close" aria-label="Close image viewer">
                <i class="bi bi-x-lg" aria-hidden="true"></i>
            </button>
            <button type="button" class="image-lightbox-nav image-lightbox-nav--prev" aria-label="Previous image">
                <i class="bi bi-chevron-left" aria-hidden="true"></i>
            </button>
            <div class="image-lightbox-stage">
                <img class="image-lightbox-image" alt="" />
            </div>
            <button type="button" class="image-lightbox-nav image-lightbox-nav--next" aria-label="Next image">
                <i class="bi bi-chevron-right" aria-hidden="true"></i>
            </button>
            <p class="image-lightbox-counter" aria-live="polite"></p>
        `
        overlay.querySelector(".image-lightbox-close").addEventListener("click", () => this.close())
        overlay.querySelector(".image-lightbox-nav--prev").addEventListener("click", () => this._step(-1))
        overlay.querySelector(".image-lightbox-nav--next").addEventListener("click", () => this._step(1))

        this._onKeydown = (e) => {
            if (e.key === "Escape") {
                e.preventDefault()
                this.close()
            } else if (e.key === "ArrowRight") {
                this._step(1)
            } else if (e.key === "ArrowLeft") {
                this._step(-1)
            }
        }
        document.addEventListener("keydown", this._onKeydown)

        document.body.appendChild(overlay)
        document.body.classList.add("image-lightbox-open")
        overlay.querySelector(".image-lightbox-close").focus()
        this._overlay = overlay
    }

    _render() {
        if (!this._overlay) return
        const item = this._items[this._index]
        const image = this._overlay.querySelector(".image-lightbox-image")
        image.src = item.url
        image.alt = item.caption
        const multiple = this._items.length > 1
        this._overlay.querySelectorAll(".image-lightbox-nav").forEach((nav) => { nav.hidden = !multiple })
        const counter = this._overlay.querySelector(".image-lightbox-counter")
        // The caption survives the position rather than being replaced by it.
        // On a post it is alt text and usually empty, so this reads exactly as
        // it did; on a family tree's scans it is the document's own name, and
        // "1 of 3" alone leaves a visitor looking at an 1878 marriage register
        // with nothing on screen saying which one it is.
        counter.textContent = [item.caption, (multiple ? `${this._index + 1} of ${this._items.length}` : null)].filter(Boolean).join(" · ")
    }
}
